import {
  type SendData,
  type CollabStateBridge,
} from '@atlassian/collab-connection-client';
import { uuidv4 } from 'lib0/random.js';

import { encodeMessageV2 } from '@canvas/protocol/v2';
import { MessageType } from '@canvas/protocol-types';
import { getUpdateMessageOrigin } from '@canvas/transaction';

import { mergeUpdate } from '../util/merge-update';

import { type Update, type AbstractBatchedUpdates } from './abstract-batched-updates';
import { debugLog, debugWarn } from './debug-logger';
import { type UpdateMetadata } from './yjs-socket';

const STATE_BRIDGE_BATCH_TIMEOUT_MS = 200;

export class StateBridge
  implements
    CollabStateBridge<Uint8Array>,
    AbstractBatchedUpdates<Uint8Array<ArrayBuffer>, UpdateMetadata>
{
  // stores all pending changes
  private buffer: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>[] = [];
  // stores the current pending outgoing updates
  private outgoing = 0;
  private outgoingId?: string;
  private observer?: (data: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>) => void;
  private flushObserver?: () => void;
  private confirmedPayloadObserver?: (payload: Uint8Array<ArrayBuffer>) => void;
  private mismatchObserver?: () => void;
  private lastSentClock: number | null = null;
  private outgoingPayload?: Uint8Array<ArrayBuffer>;
  private batchTimer: ReturnType<typeof setTimeout> | undefined;
  private destroyed = false;

  constructor(private readonly timeout = STATE_BRIDGE_BATCH_TIMEOUT_MS) {}

  addUpdate(update: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>): void {
    if (this.destroyed) {
      return;
    }

    const bufferSizeBefore = this.buffer.length;
    this.buffer.push(update);
    debugLog(
      '[StateBridge:addUpdate] buffer size before=%d → after=%d, payload bytes=%d, clock=%d, hasObserver=%s',
      bufferSizeBefore,
      this.buffer.length,
      update.payload.byteLength,
      update.clock,
      Boolean(this.observer),
    );
    if (this.outgoing === 0) {
      this.scheduleUpdate();
    }
  }

  flush() {
    if (this.destroyed) {
      return;
    }

    this.flushPendingBatch();

    if (this.buffer.length > 0) {
      this.flushObserver?.();
    }
  }

  // this will set observer to be a function that eventually calls c3.sendThenConfirm (through the abstract Transport layer)
  onUpdate(
    observer: (data: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>) => void,
  ): this {
    this.observer = observer;
    return this;
  }

  offUpdate(): this {
    this.observer = undefined;
    return this;
  }

  onFlush(flushObserver: () => void): this {
    this.flushObserver = flushObserver;
    return this;
  }

  destroy() {
    this.destroyed = true;
    this.clearScheduledUpdate();
  }

  reset() {
    this.clearScheduledUpdate();
    this.buffer = [];
    this.outgoing = 0;
    this.outgoingId = undefined;
    this.outgoingPayload = undefined;
    this.lastSentClock = null;
  }

  /**
   * Resets the buffer and silently loads a single update without firing the
   * observer. Use this when the caller wants to populate the buffer for the
   * next `retrieve()` call (e.g. from `transport.send()`) without triggering
   * an immediate `sendThenConfirm` via the observer chain.
   */
  resetAndLoad(update: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>): void {
    if (this.destroyed) {
      return;
    }

    const bufferSizeBefore = this.buffer.length;
    this.reset();
    this.buffer.push(update);
    debugLog(
      '[StateBridge:resetAndLoad] cleared %d existing buffer item(s), loaded new update: payload bytes=%d, clock=%d',
      bufferSizeBefore,
      update.payload.byteLength,
      update.clock,
    );
  }

  retrieve(): SendData<Uint8Array> | null {
    if (this.destroyed) {
      return null;
    }

    this.clearScheduledUpdate();
    // return undefined payload if empty buffer
    if (this.buffer.length < 1) {
      debugLog(
        '[StateBridge:retrieve] buffer is empty — returning null (nothing to send)',
      );
      return null;
    }
    debugLog(
      '[StateBridge:retrieve] merging %d buffer item(s) to send. Individual payload sizes: [%s]',
      this.buffer.length,
      this.buffer.map((u) => u.payload.byteLength).join(', '),
    );
    const updatesToSend = this.buffer.map((update) => ({
      ...update,
      payload: structuredClone(update.payload),
    }));
    this.outgoing = updatesToSend.length;
    this.outgoingId = uuidv4();
    const mergedUpdate = mergeUpdate(updatesToSend);
    const updateToSend = {
      name: 'update',
      payload: encodeMessageV2({
        type: MessageType.Update,
        data: {
          data: mergedUpdate.payload,
          origin: getUpdateMessageOrigin(mergedUpdate.origin),
          previousYjsClock: this.lastSentClock,
          currentYjsClock: mergedUpdate.clock,
        },
      }),
      id: this.outgoingId,
    };
    this.lastSentClock = mergedUpdate.clock;
    this.outgoingPayload = mergedUpdate.payload;
    return updateToSend;
  }

  confirm(_confirmed: SendData<Uint8Array>, id?: string): Promise<void> {
    if (this.destroyed) {
      return Promise.resolve();
    }

    // check the id to make sure it matches
    if (id !== undefined && id === this.outgoingId) {
      debugLog(
        '[StateBridge:confirm] ✅ id matched (%s), removing %d outgoing item(s), remaining buffer: %d',
        id,
        this.outgoing,
        this.buffer.length - this.outgoing,
      );
      if (this.outgoingPayload) {
        this.confirmedPayloadObserver?.(this.outgoingPayload);
      }
      // remove the first N elements from the buffer, where N is the number of updates batched in the latest send
      this.buffer = this.buffer.slice(this.outgoing);
      this.outgoing = 0;
      this.outgoingId = undefined;
      this.outgoingPayload = undefined;
      this.scheduleUpdate();
    } else if (id !== undefined) {
      debugWarn(
        '[StateBridge:confirm] ❌ id MISMATCH — expected=%s, got=%s — triggering mismatch handler',
        this.outgoingId,
        id,
      );
      // id was provided but doesn't match — signal that state is out of sync
      this.mismatchObserver?.();
    }
    return Promise.resolve();
  }

  onConfirmedPayload(observer: (payload: Uint8Array<ArrayBuffer>) => void): this {
    this.confirmedPayloadObserver = observer;
    return this;
  }

  onMismatch(observer: () => void): this {
    this.mismatchObserver = observer;
    return this;
  }

  private scheduleUpdate() {
    if (
      this.destroyed ||
      this.batchTimer ||
      this.buffer.length === 0 ||
      this.outgoing > 0
    ) {
      return;
    }

    this.batchTimer = setTimeout(this.flushPendingBatch, this.timeout);
  }

  private flushPendingBatch = () => {
    this.clearScheduledUpdate();

    if (this.destroyed || this.buffer.length === 0 || this.outgoing > 0) {
      return;
    }

    this.observer?.(mergeUpdate(this.buffer));
  };

  private clearScheduledUpdate() {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = undefined;
    }
  }
}
